/**
 * "Abrir App" e "Voltar ao SIAG".
 *
 * O portal é uma rota da MESMA aplicação (docs/manutencao/02). Não há deep link,
 * novo login nem outro domínio: a sessão vive no localStorage da origem, então
 * qualquer aba ou janela instalada da mesma origem já está autenticada.
 *
 * O que muda por contexto é só ONDE abrir:
 * - desktop (ponteiro fino): nova aba — o gestor mantém a tela gerencial aberta;
 * - celular/tablet (ponteiro grosso): mesma aba — nova aba no celular vira aba
 *   perdida no seletor;
 * - já rodando instalado (standalone): mesma janela — `window.open` tiraria o
 *   usuário do app.
 */
export const PORTAL_BASE = "/app/frota";
export const SIAG_HOME = "/";

export type OpenMode = "new-tab" | "same-tab";

export interface DisplayContext {
  coarsePointer: boolean;
  standalone: boolean;
}

export function resolveOpenMode(ctx: DisplayContext): OpenMode {
  if (ctx.standalone || ctx.coarsePointer) return "same-tab";
  return "new-tab";
}

/** Lê o contexto do navegador. Fora do browser (teste/SSR) assume desktop. */
export function readDisplayContext(win: Pick<Window, "matchMedia"> | undefined = globalThis.window): DisplayContext {
  if (!win?.matchMedia) return { coarsePointer: false, standalone: false };
  const nav = (globalThis.navigator ?? {}) as Navigator & { standalone?: boolean };
  return {
    coarsePointer: win.matchMedia("(pointer: coarse)").matches,
    standalone: win.matchMedia("(display-mode: standalone)").matches || nav.standalone === true,
  };
}

export interface Navigate {
  assign: (url: string) => void;
  open: (url: string, target: string, features: string) => unknown;
}

/** Abre o portal. Devolve o modo usado (útil para teste e telemetria). */
export function openPortal(
  path: string = PORTAL_BASE,
  ctx: DisplayContext = readDisplayContext(),
  nav: Navigate = { assign: (u) => window.location.assign(u), open: (u, t, f) => window.open(u, t, f) },
): OpenMode {
  const mode = resolveOpenMode(ctx);
  if (mode === "new-tab") {
    // Pop-up bloqueado devolve null: cai para a mesma aba em vez de não fazer nada.
    const win = nav.open(path, "_blank", "noopener");
    if (win === null) {
      nav.assign(path);
      return "same-tab";
    }
    return "new-tab";
  }
  nav.assign(path);
  return "same-tab";
}

/** O caminho pertence ao portal? */
export function isPortalPath(pathname: string): boolean {
  return pathname === PORTAL_BASE || pathname.startsWith(`${PORTAL_BASE}/`);
}
